<template>
    <div class="evasion-chart" v-resize="resize" ref="container">
        <svg
            :viewBox="`0 0 ${boundWidth} ${boundHeight}`"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            @dblclick.prevent="download"
            ref="svg"
            :style="{ height: boundHeight, width: boundWidth }"
        >
            <g font-family="Lato">
                <!-- background -->
                <rect x="0" y="0" :width="boundWidth" :height="boundHeight" fill="white" />
                <g :transform="`translate(${padding}, ${padding})`">
                    <g>
                        <g>
                            <rect x="0" y="0" width="22" height="22" stroke="none" :fill="outlierColor" />
                            <text x="30" y="11" fill="black" stroke="none" dominant-baseline="central">Outlier</text>
                        </g>
                        <g transform="translate(96,0)">
                            <g transform="translate(11, 11)">
                                <circle r="11" :fill="outlierColor" stroke="none" />
                                <text fill="white" dominant-baseline="central" text-anchor="middle">P</text>
                            </g>
                            <text x="30" y="11" fill="black" stroke="none" dominant-baseline="central">
                                Predictive Outlier
                            </text>
                        </g>
                        <g transform="translate(256, 0)" v-if="averageEvasionLine !== null">
                            <line x1="0" x2="22" y1="11" y2="11" :stroke="dateColor" stroke-width="2" />
                            <text x="30" y="11" fill="black" stroke="none" dominant-baseline="central">
                                Evasion Average
                            </text>
                        </g>
                    </g>
                    <g :transform="`translate(0, ${legendHeight})`">
                        <g :fill="evasionColor">
                            <!-- left axis, linear scale from 0-100%; all the text widths are known ahead of time -->
                            <g :transform="`rotate(-90, 8, ${chartAreaHeight / 2})`">
                                <text x="8" :y="chartAreaHeight / 2" text-anchor="middle" font-weight="bold">
                                    Evasion %
                                </text>
                            </g>
                            <g font-size="12px">
                                <text
                                    v-for="v in evasionAxis"
                                    :key="v.key"
                                    :x="evasionAxisWidth"
                                    :y="v.y"
                                    text-anchor="end"
                                    dominant-baseline="middle"
                                >
                                    {{ v.label }}
                                </text>
                            </g>
                        </g>
                        <g :transform="`translate(${chartAreaLeft}, 0)`">
                            <!-- chart area-->
                            <g v-if="alphaLine !== null">
                                <!-- alpha series-->
                                <path stroke="none" :d="alphaFill" :fill="alphaFillColor" />
                                <path :stroke="alphaColor" :d="alphaLine" fill="none" />
                                <line
                                    x1="0"
                                    :x2="chartAreaWidth"
                                    :y1="alphaOriginLine"
                                    :y2="alphaOriginLine"
                                    :stroke="alphaColor"
                                    stroke-dasharray="1 1"
                                />
                            </g>
                            <g>
                                <!-- average evasion-->
                                <line
                                    x1="0"
                                    :x2="chartAreaWidth"
                                    :y1="averageEvasionLine"
                                    :y2="averageEvasionLine"
                                    :stroke="dateColor"
                                    stroke-width="2"
                                    v-if="averageEvasionLine !== null"
                                />
                            </g>
                            <g>
                                <!-- evasion series-->
                                <rect
                                    v-for="bar in evasionSeries"
                                    :key="bar.key"
                                    :x="bar.x"
                                    :y="bar.y"
                                    :height="bar.height"
                                    :width="bar.width"
                                    :fill="bar.color"
                                    @click="e => showTooltip(e, [bar.transcript])"
                                    @mouseover="e => showTooltip(e, [bar.transcript])"
                                    @mouseout="() => hideTooltip(false)"
                                />
                            </g>
                            <!-- predictive outliers -->
                            <g
                                v-for="marker in evasionPredictiveOutliersSeries"
                                :key="marker.key"
                                :transform="`translate(${marker.x},${marker.y - 11})`"
                            >
                                <circle r="11" :fill="outlierColor" stroke="none" />
                                <text fill="white" dominant-baseline="central" text-anchor="middle">P</text>

                                <circle
                                    r="11"
                                    fill="transparent"
                                    stroke="none"
                                    @click="e => showTooltip(e, [marker.transcript])"
                                    @mouseover="e => showTooltip(e, [marker.transcript])"
                                    @mouseout="() => hideTooltip(false)"
                                />
                            </g>
                        </g>
                        <g :fill="alphaColor" :transform="`translate(${contentWidth - alphaAxisWidth}, 0)`">
                            <!-- alpha axis -->
                            <g
                                :transform="`rotate(90, ${alphaAxisWidth - 8}, ${chartAreaHeight / 2})`"
                                v-if="alphaLine !== null"
                            >
                                <text
                                    :x="alphaAxisWidth - 8"
                                    :y="chartAreaHeight / 2"
                                    text-anchor="middle"
                                    font-weight="bold"
                                >
                                    Company Alpha
                                </text>
                            </g>
                            <g font-size="12px" ref="svgAlphaAxisLabels">
                                <text
                                    v-for="label in alphaAxis"
                                    :key="label.key"
                                    x="0"
                                    :y="label.y"
                                    text-anchor="start"
                                    dominant-baseline="middle"
                                >
                                    {{ label.label }}
                                </text>
                            </g>
                        </g>
                        <g
                            :transform="`translate(${chartAreaLeft}, ${chartAreaHeight + 16})`"
                            :fill="dateColor"
                            font-size="12px"
                            font-style="italic"
                            font-weight="light"
                        >
                            <!-- date axis -->
                            <text
                                v-for="label in dateAxis"
                                :key="label.key"
                                :x="label.x"
                                y="0"
                                :transform="`rotate(-45, ${label.x}, 0)`"
                                dominant-baseline="middle"
                                text-anchor="end"
                                @click="e => showTooltip(e, label.transcripts)"
                                @mouseover="e => showTooltip(e, label.transcripts)"
                                @mouseout="e => hideTooltip(false)"
                            >
                                {{ label.label }}
                            </text>
                        </g>
                    </g>
                </g>
            </g>
        </svg>
        <div
            class="modal"
            :data-tooltips-length="tooltips.length"
            :style="tooltipStyle"
            v-if="tooltips.length > 0"
            v-click-outside="() => hideTooltip(true)"
            @mouseover="e => showTooltip(e, tooltips)"
        >
            <a
                href="#"
                @click.prevent="previousTooltip"
                class="material-icons"
                :disabled="tooltipIndex <= 0"
                v-if="tooltips.length > 1"
                >chevron_left</a
            >
            <TranscriptSummaryBox :transcript="tooltips[tooltipIndex]" />
            <a
                href="#"
                @click.prevent="nextTooltip"
                class="material-icons"
                :disabled="tooltipIndex >= tooltips.length - 1"
                v-if="tooltips.length > 1"
                >chevron_right</a
            >
        </div>
    </div>
</template>
<script setup>
import TranscriptSummaryBox from './TranscriptSummaryBox.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { asyncComputed } from '@/utils/asyncComputed'

const props = defineProps({
    averageEvasion: {
        type: Number,
        default: null,
    },
    companyId: {
        type: [Number, String],
        required: true,
    },
    endDate: {
        type: [Date, String],
        required: false,
    },
    transcripts: {
        type: Array,
        required: true,
    },
})

const container = ref(null)

const tooltips = ref([])
const tooltipIndex = ref(0)
const tooltipsBy = ref(null)
const tooltipStyle = ref({})
const tooltipTimeout = ref(null)

function nextTooltip() {
    if (tooltipIndex.value < tooltips.value.length - 1) {
        tooltipIndex.value++
    }
}

function previousTooltip() {
    if (tooltipIndex.value > 0) {
        tooltipIndex.value--
    }
}

function showTooltip(evt, transcripts) {
    if (tooltipTimeout.value) {
        clearTimeout(tooltipTimeout.value)
        tooltipTimeout.value = null
    }

    tooltipsBy.value = evt.type

    if (evt.currentTarget.classList.contains('modal')) {
        // don't reposition if we hop into the modal
        return
    }

    tooltips.value = transcripts
    tooltipIndex.value = 0

    const containerBounds = container.value.getBoundingClientRect()
    const targetBounds = evt.currentTarget.getBoundingClientRect()

    const style = {}

    const computeRight = targetBounds.left > (containerBounds.left + containerBounds.right) / 2

    switch (evt.currentTarget.tagName) {
        case 'circle':
            style.bottom = `${containerBounds.bototm - targetBounds.bottom}px`

            if (computeRight) {
                style.right = `${containerBounds.right - targetBounds.left - 8}px`
            } else {
                style.left = `${targetBounds.right - containerBounds.left - 8}px`
            }
            break
        case 'rect':
            style.bottom = `${containerBounds.bottom - targetBounds.top}px`

            if (computeRight) {
                style.right = `${containerBounds.right - targetBounds.left}px`
            } else {
                style.left = `${targetBounds.right - containerBounds.left}px`
            }
            break
        case 'text':
            style.bottom = `${containerBounds.bottom - targetBounds.top}px`

            if (computeRight) {
                style.right = `${containerBounds.right - targetBounds.right + 7}px`
            } else {
                style.left = `${targetBounds.right - containerBounds.left}px`
            }
            break
    }

    tooltipStyle.value = style
}
function hideTooltip(force) {
    if (tooltipTimeout.value) {
        window.clearTimeout(tooltipTimeout.value)
    }

    if (force || tooltipsBy.value !== 'click') {
        tooltipTimeout.value = window.setTimeout(() => {
            tooltips.value = []
        }, 250)
    }
}

const evasionColor = 'rgb(10, 52, 171)'
const alphaColor = 'rgb(0, 201, 234)'
const dateColor = 'rgb(137, 135, 140)'

const padding = ref(24)
const legendHeight = ref(64)
const boundWidth = ref(960)
const boundHeight = ref(320)

const contentHeight = computed(() => boundHeight.value - 2 * padding.value)
const contentWidth = computed(() => boundWidth.value - 2 * padding.value)

const dateAxisHeight = ref(32)
const evasionAxisWidth = ref(56)
const alphaAxisWidth = ref(0)

const chartAreaLeft = computed(() => evasionAxisWidth.value + 8)
const chartAreaTop = computed(() => legendHeight.value + 8)
const chartAreaHeight = computed(() => contentHeight.value - chartAreaTop.value - dateAxisHeight.value - 8)
const chartAreaWidth = computed(() => contentWidth.value - chartAreaLeft.value - alphaAxisWidth.value - 8)

/* evasion axis (fixed) */
const evasionScale = computed(() => {
    const height = chartAreaHeight.value

    return function (pct) {
        return height - height * pct
    }
})

const evasionAxis = computed(() => {
    const scale = evasionScale.value

    return [0, 20, 40, 60, 80, 100].map(i => ({
        key: `la-${i}%`,
        label: `${i}%`,
        y: scale(i / 100),
    }))
})

/* date axis */
import { addDays, startOfDay } from '@/utils/date.js'

const startDate = computed(() => {
    if (props.transcripts.length === 0) {
        return addDays(-30, startOfDay())
    }

    return addDays(
        -30,
        startOfDay(
            new Date(
                props.transcripts.reduce((p, c) => (p.localeCompare(c.eventDate) < 0 ? p : c.eventDate), '9999-12-31')
            )
        )
    )
})

const dateScale = computed(() => {
    const inputStart = startDate.value.valueOf()
    const inputEnd = props.endDate instanceof Date ? props.endDate.valueOf() : new Date(props.endDate).valueOf()
    const inputRange = inputEnd - inputStart

    const outputRange = chartAreaWidth.value

    return function (date) {
        return ((date.valueOf() - inputStart) / inputRange) * outputRange
    }
})

function averageDate(dates) {
    if (dates.length === 1) {
        return new Date(dates[0])
    }

    return new Date(dates.reduce((p, c) => p + new Date(c).valueOf(), 0) / dates.length)
}

const quarterGroups = computed(() => {
    const groups = groupBy(props.transcripts, t => t.fiscalQuarter)

    return Object.entries(groups).map(([fiscalQuarter, transcripts]) => {
        return {
            date: averageDate(transcripts.map(t => t.eventDate)),
            fiscalQuarter,
            transcripts,
        }
    })
})

const dateAxis = computed(() => {
    const scale = dateScale.value

    return quarterGroups.value.map(g => {
        return {
            ...g,
            key: g.fiscalQuarter,
            label: g.fiscalQuarter,
            x: scale(g.date),
        }
    })
})

/* evasion series */
const outlierColor = 'rgb(255, 59, 48)'
const barWidth = ref(5)
const barGap = ref(2)

const evasionSeries = computed(() => {
    const scaleX = dateScale.value
    const scaleY = evasionScale.value
    const width = barWidth.value
    const gap = barGap.value
    const step = width + gap
    const adjust = width / 2

    const zeroY = scaleY(0)

    return quarterGroups.value.flatMap(g => {
        let next = scaleX(g.date)

        const visible = g.transcripts.filter(t => (t.evasion?.evasivePercentage ?? 0) > 0)

        if (visible.length === 0) {
            return []
        }

        if (visible.length > 1) {
            next -= visible.length * width - (visible.length - 1) * gap
        }

        return visible.map(t => {
            const x = next - adjust
            const y = scaleY(t.evasion?.evasivePercentage ?? 0)

            next += step

            return {
                key: `t-${t.id}`,
                x,
                y,
                width,
                height: zeroY - y,
                color: t.evasion?.outlier ? outlierColor : evasionColor,
                transcript: t,
            }
        })
    })
})

/* evasion predictive outliers */
const evasionPredictiveOutliersSeries = computed(() => {
    return evasionSeries.value
        .filter(p => p.transcript.evasion?.outlierPredictive)
        .map(p => ({
            ...p,
            key: p.key + '-o',
            x: p.x + p.width / 2,
            y: p.y + 1,
        }))
})

/* average evasion line */
const averageEvasionLine = computed(() => {
    if (props.averageEvasion === null) {
        return null
    }

    return evasionScale.value(props.averageEvasion)
})

/* alpha data */
import { getCompanyAlpha } from '@/services/alphaService.js'

const alphaContent = asyncComputed(signal => {
    const endDate = props.endDate?.toISOString ? props.endDate.toISOString() : props.endDate
    return getCompanyAlpha(props.companyId, { startDate: startDate.value, endDate }, signal)
})

const alpha = computed(() => alphaContent.value?.daily ?? [])

/* alpha axis */
import { computeRangeAndStep } from '@/components/charts/utils.js'
import { formatNegativeParens } from '@/utils/format.js'

const alphaMinMaxStep = computed(() => {
    if (alpha.value.length === 0) {
        return { min: 0, max: 1 }
    }

    let min = Number.MAX_VALUE
    let max = Number.MIN_VALUE

    alpha.value.forEach(a => {
        if (a.value < min) {
            min = a.value
        } else if (a.value > max) {
            max = a.value
        }
    })

    return computeRangeAndStep(min, max)
})

const alphaScale = computed(() => {
    const { min, max } = alphaMinMaxStep.value

    const inputStart = min
    const inputRange = max - min

    const outputRange = chartAreaHeight.value

    return function (val) {
        return outputRange - ((val - inputStart) / inputRange) * outputRange
    }
})

const alphaAxis = computed(() => {
    if (alpha.value.length === 0) {
        return []
    }

    const { min, max, step } = alphaMinMaxStep.value
    const scale = alphaScale.value

    let y = min

    const labels = []

    while (y <= max) {
        labels.push({
            key: `alpha-${y}`,
            label: formatNegativeParens(y, { style: 'percent' }),
            y: scale(y),
        })

        y += step
    }

    return labels
})

// need to adjust for the actual width on this
const svgAlphaAxisLabels = ref(null)

function updateAlphaAxisWidth() {
    if (!svgAlphaAxisLabels.value) {
        return
    }

    alphaAxisWidth.value = svgAlphaAxisLabels.value.getBBox().width + 8 + 16
}

watch(alphaAxis, () => nextTick(updateAlphaAxisWidth))

/* alpha zero line */
const alphaOriginLine = computed(() => {
    return alphaScale.value(0)
})

/* alpha series */
const alphaSeries = computed(() => {
    const scaleX = dateScale.value
    const scaleY = alphaScale.value

    return alpha.value.map(a => ({
        key: `a-${a.date}`,
        x: scaleX(new Date(a.date)),
        y: scaleY(a.value),
    }))
})

const alphaPath = computed(() => {
    const series = alphaSeries.value

    if (series.length === 0) {
        return null
    }

    return series.map(p => `L${p.x},${p.y}`).join(' ')
})

const alphaLine = computed(() => {
    const path = alphaPath.value

    if (path == null) {
        return null
    }

    return `M${path.substring(1)}`
})

const alphaFillColor = 'rgb(234, 251, 253)'

const alphaFill = computed(() => {
    const path = alphaPath.value

    if (path == null) {
        return null
    }

    const first = alphaSeries.value[0]
    const last = alphaSeries.value[alphaSeries.value.length - 1]

    return `M${path.substring(1)} L${last.x},${chartAreaHeight.value} L${first.x},${chartAreaHeight.value} Z`
})

/* resize handling */
function resize(evt) {
    if (evt.contentRect) {
        boundHeight.value = evt.contentRect.height
        boundWidth.value = evt.contentRect.width
    }
}

/* download */
import { downloadPng } from '@/utils/svg.js'
import { groupBy } from '@/utils/array'

const svg = ref(null)

function download() {
    downloadPng(svg.value, 'evasion-chart.png')
}
</script>
<style scoped>
.evasion-chart {
    padding: 8px;
    position: relative;
}

svg {
    left: 8px;
    position: absolute;
    top: 8px;
}

.modal {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 16px;
    min-width: initial;
    padding: 16px 8px;
    position: absolute;
    z-index: var(--z-index-tooltip);

    & > a[disabled]:not(a[disabled='false']) {
        opacity: 0.25;
    }

    &[data-tooltips-length='1'] {
        padding: 16px 24px;
    }
}

.transcript-summary-box {
    width: 360px;
}
</style>
